import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
// NOTE: This module is a pure utility (not a React component). Do not use React hooks here.

// This file generates a single Excel workbook from:
// - tabular data (from Google Sheets-like payloads)
// - one or more diagram images (canvas/data-url/svg/remote url)
// It also normalizes images to avoid transparency artifacts in Excel and applies
// consistent header styling + lightweight formatting (auto-fit, numeric parsing).

// const DISCLAIMER_TEXT = 'Disclaimer: This projection is provided as a complimentary service and does not constitute a performance warranty';

// Map mime to ExcelJS extension
const toExcelExt = (mime) => {
  const m = String(mime || '').toLowerCase();
  if (m.includes('png')) return 'png';
  if (m.includes('jpg') || m.includes('jpeg')) return 'jpeg';
  return 'png';
};

// Apply consistent header styling with blue background and white text (cell-level)
const applyBlueHeaderStyle = (row) => {
  try {
    row.eachCell(cell => {
      cell.font = {
        ...(cell.font || {}),
        bold: true,
        size: 12,
        color: { argb: 'FFFFFFFF' }, // white text
      };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF0062A9' }, // RGB(0, 0.384, 0.663) dark blue background
      };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
    });
  } catch { /* noop */ }
};

/**
 * Write structured data into worksheet
 * @param {object} sheet - ExcelJS worksheet
 * @param {any} data - Data to write
 * @param {number|number[]} headerRowCount - Number of header rows per table (default: 1)
 * @param {number} tableIndex - Current table index when processing multiple tables
 */
const writeDataToSheet = (sheet, data, headerRowCount = 1, tableIndex = 0) => {
  // Get header count for current table
  const getHeaderCount = (idx) => {
    if (Array.isArray(headerRowCount)) {
      return headerRowCount[idx] || 1;
    }
    return headerRowCount;
  };

  // 2D array
  if (Array.isArray(data) && data.length && Array.isArray(data[0])) {
    const startRow = sheet.rowCount + 1;
    data.forEach(row => sheet.addRow(row));
    // Style header rows based on current table's header count
    const numHeaders = getHeaderCount(tableIndex);
    for (let i = 0; i < Math.min(numHeaders, data.length); i += 1) {
      const headerRow = sheet.getRow(startRow + i);
      applyBlueHeaderStyle(headerRow);
    }
    return;
  }

  // Array of objects
  if (Array.isArray(data) && data.length && typeof data[0] === 'object' && !Array.isArray(data[0])) {
    const startRow = sheet.rowCount + 1;
    // Add header row from object keys
    const keys = Object.keys(data[0]);
    sheet.addRow(keys);
    // Add data rows
    data.forEach(row => {
      const rowData = keys.map(key => row[key]);
      sheet.addRow(rowData);
    });
    // Style header rows based on current table's header count
    const numHeaders = getHeaderCount(tableIndex);
    for (let i = 0; i < Math.min(numHeaders, data.length + 1); i += 1) {
      const headerRow = sheet.getRow(startRow + i);
      applyBlueHeaderStyle(headerRow);
    }
    return;
  }

  // Object with nested sections (multiple tables)
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const entries = Object.entries(data);
    const allValuesAreArrays = entries.length > 0 && entries.every(([, v]) => Array.isArray(v));

    if (allValuesAreArrays) {
      // Each entry is a separate table, use corresponding header count from array
      entries.forEach(([, arr], idx) => {
        writeDataToSheet(sheet, arr, headerRowCount, idx);
        if (idx < entries.length - 1) sheet.addRow([]);
      });
      return;
    }

    entries.forEach(([section, value], idx) => {
      // Section title styled as blue bold (acts like a header)
      const titleRow = sheet.addRow([section]);
      applyBlueHeaderStyle(titleRow);
      sheet.addRow([]);

      if (Array.isArray(value)) {
        writeDataToSheet(sheet, value, headerRowCount, idx);
      } else {
        // Key/Value header styled blue
        const kvHeader = sheet.addRow(['Key', 'Value']);
        applyBlueHeaderStyle(kvHeader);
        Object.entries(value || {}).forEach(([k, v]) => sheet.addRow([k, v]));
      }

      sheet.addRow([]);
      sheet.addRow([]);
    });
  }
};

/**
 * Convert SVG string to PNG base64 using canvas
 */
const svgToBase64PNG = (svgString, width = 600, height = 400) => {
  return new Promise((resolve) => {
    try {
      const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        URL.revokeObjectURL(url);
        const dataUrl = canvas.toDataURL('image/png');
        const base64 = dataUrl.split(',')[1];
        resolve({ base64, ext: 'png' });
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
      img.src = url;
    } catch {
      resolve(null);
    }
  });
};

/**
 * Convert a raster image data URL (typically PNG with transparency) into a PNG
 * with a solid white background.
 */
const rasterDataUrlToWhitePng = (dataUrl) => {
  return new Promise((resolve) => {
    try {
      if (typeof dataUrl !== 'string' || !dataUrl.startsWith('data:')) {
        resolve(null);
        return;
      }

      const img = new Image();
      img.onload = () => {
        try {
          const width = img.naturalWidth || img.width || 0;
          const height = img.naturalHeight || img.height || 0;
          if (!width || !height) {
            resolve(null);
            return;
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);
          const out = canvas.toDataURL('image/png');
          resolve({ base64: out.split(',')[1], ext: 'png' });
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = dataUrl;
    } catch {
      resolve(null);
    }
  });
};

/**
 * Normalize any input into { base64, ext }
 * Accepts: data URL string, remote URL string, HTMLCanvasElement, SVG string, or object with { src, width, height, svg }
 */
const ensureImageBase64 = (input) => {
  try {
    // Canvas element or object exposing toDataURL
    if (input && typeof input === 'object' && typeof input.toDataURL === 'function') {
      try {
        const width = input.width || input.canvas?.width || 0;
        const height = input.height || input.canvas?.height || 0;
        if (width && height) {
          const out = document.createElement('canvas');
          out.width = width;
          out.height = height;
          const ctx = out.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, width, height);
          // draw original canvas onto white background
          ctx.drawImage(input, 0, 0, width, height);
          const dataUrl = out.toDataURL('image/png');
          const base64 = dataUrl.split(',')[1];
          return Promise.resolve({ base64, ext: 'png' });
        }
      } catch {
        // fall through to basic toDataURL
      }
      const dataUrl = input.toDataURL('image/png');
      const base64 = dataUrl.split(',')[1];
      return Promise.resolve({ base64, ext: 'png' });
    }
    // Object with SVG string
    if (input && typeof input === 'object' && typeof input.svg === 'string') {
      return svgToBase64PNG(input.svg, input.width || 600, input.height || 400);
    }
    // Object with src (and optional width/height)
    if (input && typeof input === 'object' && typeof input.src === 'string') {
      return ensureImageBase64(input.src.trim());
    }
    // SVG string (starts with <svg or <?xml)
    if (typeof input === 'string' && (input.trim().startsWith('<svg') || input.trim().startsWith('<?xml'))) {
      return svgToBase64PNG(input);
    }
    // Data URL string
    if (typeof input === 'string' && input.trim().startsWith('data:')) {
      const dataUrl = input.trim();
      const headerEnd = dataUrl.indexOf(',');
      const header = headerEnd > 0 ? dataUrl.slice(0, headerEnd) : '';
      const base64 = headerEnd > 0 ? dataUrl.slice(headerEnd + 1) : '';
      const mimeMatch = header.match(/^data:([^;]+);/);
      const mime = mimeMatch ? mimeMatch[1] : 'image/png';

      // If the flowsheet diagram is exported as a PNG with transparency,
      // composite it onto a white background so Excel renders it with white.
      if (String(mime).toLowerCase().includes('image/png')) {
        return rasterDataUrlToWhitePng(dataUrl).then(r => r || ({ base64, ext: 'png' }));
      }

      return Promise.resolve({ base64, ext: toExcelExt(mime) });
    }
    // Remote URL string
    if (typeof input === 'string') {
      return fetch(input, { mode: 'cors' })
        .then(res => res.blob())
        .then(blob =>
          new Promise(resolve => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const result = String(reader.result || '');
              const base64 = result.split(',')[1];
              const ext = toExcelExt(blob.type);

              if (String(blob.type).toLowerCase().includes('png') && result.startsWith('data:')) {
                rasterDataUrlToWhitePng(result)
                  .then(r => resolve(r || ({ base64, ext })));
                return;
              }

              resolve({ base64, ext });
            };
            reader.readAsDataURL(blob);
          })
        );
    }
    // Unsupported: return empty so caller can skip
    return Promise.resolve(null);
  } catch {
    return Promise.resolve(null);
  }
};

const parseSvgDims = (svgString) => {
  try {
    if (typeof svgString !== 'string') return null;
    const svg = svgString.trim();

    // width/height attributes can be plain numbers or with units (px)
    const widthMatch = svg.match(/\bwidth\s*=\s*"([0-9]+(?:\.[0-9]+)?)(?:px)?"/i);
    const heightMatch = svg.match(/\bheight\s*=\s*"([0-9]+(?:\.[0-9]+)?)(?:px)?"/i);
    if (widthMatch && heightMatch) {
      const width = Number(widthMatch[1]);
      const height = Number(heightMatch[1]);
      if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
        return { width, height };
      }
    }

    // Fall back to viewBox="minX minY width height"
    const viewBoxMatch = svg.match(/\bviewBox\s*=\s*"\s*[-0-9.]+\s+[-0-9.]+\s+([0-9.]+)\s+([0-9.]+)\s*"/i);
    if (viewBoxMatch) {
      const width = Number(viewBoxMatch[1]);
      const height = Number(viewBoxMatch[2]);
      if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
        return { width, height };
      }
    }

    return null;
  } catch {
    return null;
  }
};

/**
 * Compute image dimensions. If input provides width/height, scale to maxWidth.
 */
const getImageDims = (input, maxWidth = 900) => {
  try {
    if (input && typeof input === 'object' && typeof input.width === 'number' && typeof input.height === 'number') {
      const scale = Math.min(maxWidth / input.width, 1);
      return { width: Math.round(input.width * scale), height: Math.round(input.height * scale) };
    }

    // If SVG provided but width/height omitted, try to parse from the SVG string.
    if (input && typeof input === 'object' && typeof input.svg === 'string') {
      const dims = parseSvgDims(input.svg);
      if (dims) {
        const scale = Math.min(maxWidth / dims.width, 1);
        return { width: Math.round(dims.width * scale), height: Math.round(dims.height * scale) };
      }
    }
  } catch { /* noop */ }
  return { width: 900, height: 450 };
};

const normalizeDiagramInputs = (diagramMap, keyPredicate) => {
  const out = [];
  try {
    Object.entries(diagramMap || {}).forEach(([key, value]) => {
      if (!keyPredicate(key)) return;

      if (Array.isArray(value)) {
        value.forEach((v, idx) => {
          if (!v) return;
          const label = v?.text2 || v?.text || `${key} ${idx + 1}`;
          out.push({ label, imageInput: v });
        });
        return;
      }

      out.push({ label: key, imageInput: value });
    });
  } catch {
    // noop
  }
  return out;
};

/**
 * Insert image into worksheet (supports data URLs, remote URLs, canvas, objects with src)
 * @param {Object} workbook - ExcelJS workbook
 * @param {Object} sheet - ExcelJS worksheet
 * @param {any} imageInput - Image source (data URL, canvas, object with src/svg, etc.)
 * @param {number} startRow - Optional row number to position the image (default: 2)
 */
const insertImage = (workbook, sheet, imageInput, startRow = 2) =>
  ensureImageBase64(imageInput).then(img => {
    if (!img || !img.base64) return; // fail-soft
    const dims = getImageDims(imageInput);
    const imageId = workbook.addImage({
      base64: img.base64,
      extension: img.ext || 'png',
    });
    sheet.addImage(imageId, {
      tl: { col: 1, row: startRow },
      ext: dims,
    });
  });

// Merge vertically contiguous duplicate values in a specific column.
const mergeVerticalDuplicates = (sheet, col = 1, startRow = 2, endRow) => {
  const colIndex = Number.parseInt(col, 10);
  const startRowIndex = Number.parseInt(startRow, 10);
  const lastRowIndex = Number.parseInt((endRow ?? sheet.rowCount), 10);

  if (!Number.isFinite(colIndex) || colIndex <= 0) return;
  if (!Number.isFinite(startRowIndex) || startRowIndex <= 0) return;
  if (!Number.isFinite(lastRowIndex) || lastRowIndex < startRowIndex) return;

  const getCellValue = (r) => {
    const rowIndex = Number.parseInt(r, 10);
    if (!Number.isFinite(rowIndex) || rowIndex <= 0) return '';
    const val = sheet.getRow(rowIndex)?.getCell(colIndex)?.value;
    return val == null ? '' : String(val).trim();
  };

  let runStart = startRowIndex;
  let runValue = getCellValue(startRowIndex);

  for (let r = startRowIndex + 1; r <= lastRowIndex + 1; r += 1) {
    const currentValue = getCellValue(r);
    if (currentValue !== runValue || currentValue === '' || r > lastRowIndex) {
      // Merge if we have a run of 2+ identical non-empty values
      if (runValue !== '' && r - runStart > 1) {
        try {
          sheet.mergeCells(runStart, colIndex, r - 1, colIndex);
          sheet.getRow(runStart).getCell(colIndex).alignment = { vertical: 'middle', horizontal: 'left' };
        } catch { /* noop */ }
      }
      runStart = r;
      runValue = currentValue;
    }
  }
};

const roundTo2 = (num) => {
  const n = Number(num);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100) / 100;
};

const tryParseNumeric = (value) => {
  if (typeof value === 'number') return roundTo2(value);
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (trimmed === '') return null;

  // allow thousands separators
  const normalized = trimmed.replace(/,/g, '');
  const numericLike = /^-?(?:\d+\.?\d*|\.\d+)$/.test(normalized);
  if (!numericLike) return null;

  return roundTo2(Number(normalized));
};

// Merge horizontally contiguous duplicate values, but ONLY for header cells (blue fill).
// This keeps merges confined to headers (including multi-row headers), and avoids merging
// across body/data rows.
const mergeHorizontalDuplicatesInHeaders = (sheet, { startCol = 1, endCol, startRow = 1, endRow } = {}) => {
  const startColIndex = Number.parseInt(startCol, 10);
  const startRowIndex = Number.parseInt(startRow, 10);
  const lastColIndex = Number.parseInt((endCol ?? sheet.columnCount), 10);
  const lastRowIndex = Number.parseInt((endRow ?? sheet.rowCount), 10);

  if (!Number.isFinite(startColIndex) || startColIndex <= 0) return;
  if (!Number.isFinite(startRowIndex) || startRowIndex <= 0) return;
  if (!Number.isFinite(lastColIndex) || lastColIndex < startColIndex) return;
  if (!Number.isFinite(lastRowIndex) || lastRowIndex < startRowIndex) return;

  const isBlueHeader = (cell) => {
    try {
      return cell?.fill?.fgColor?.argb === 'FF0062A9';
    } catch {
      return false;
    }
  };

  const getHeaderText = (r, c) => {
    const rowIndex = Number.parseInt(r, 10);
    if (!Number.isFinite(rowIndex) || rowIndex <= 0) return '';
    const cell = sheet.getRow(rowIndex)?.getCell(c);
    if (!cell || !isBlueHeader(cell)) return '';
    const val = cell.value;
    if (val == null) return '';
    if (val instanceof Date) return '';
    if (typeof val === 'object') return '';
    const str = String(val).trim();
    return str;
  };

  for (let r = startRowIndex; r <= lastRowIndex; r += 1) {
    let runStartCol = startColIndex;
    let runValue = getHeaderText(r, startColIndex);

    for (let c = startColIndex + 1; c <= lastColIndex + 1; c += 1) {
      const currentValue = getHeaderText(r, c);
      if (currentValue !== runValue || currentValue === '' || c > lastColIndex) {
        if (runValue !== '' && c - runStartCol > 1) {
          try {
            sheet.mergeCells(r, runStartCol, r, c - 1);
            sheet.getRow(r).getCell(runStartCol).alignment = { vertical: 'middle', horizontal: 'center' };
          } catch { /* noop */ }
        }
        runStartCol = c;
        runValue = currentValue;
      }
    }
  }
};

const normalizeHeaderRowCount = (value) => {
  if (Array.isArray(value)) {
    const arr = value
      .map(v => {
        const n = Number.parseInt(v, 10);
        return Number.isFinite(n) && n > 0 ? n : 1;
      });
    return arr.length ? arr : 1;
  }

  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) && n > 0 ? n : 1;
};

const isBlueHeaderCell = (cell) => {
  try {
    return cell?.fill?.fgColor?.argb === 'FF0062A9';
  } catch {
    return false;
  }
};

const applyTwoDecimalNumberFormat = (sheet) => {
  try {
    sheet.eachRow((row) => {
      row.eachCell((cell, colNumber) => {
        // Exclude the first column (A)
        if (colNumber === 1) return;
        if (isBlueHeaderCell(cell)) return;

        const v = cell.value;
        // Skip complex ExcelJS value types (formula, hyperlink, richText, etc.)
        if (v && typeof v === 'object' && !(v instanceof Date)) return;

        const n = tryParseNumeric(v);
        if (n == null) return;

        cell.value = n;
        cell.numFmt = '0.00';
      });
    });
  } catch { /* noop */ }
};

const ensureMinRowCount = (sheet, minRows) => {
  try {
    const target = Number.parseInt(minRows, 10);
    if (!Number.isFinite(target) || target <= 0) return;
    while (sheet.rowCount < target) {
      sheet.addRow([]);
    }
  } catch { /* noop */ }
};

const imageHeightToRowSpan = (heightPx) => {
  const h = Number(heightPx);
  if (!Number.isFinite(h) || h <= 0) return 0;
  // Empirically aligned with existing constants (450px ≈ 25 rows)
  return Math.max(1, Math.ceil(h / 18));
};

const appendDisclaimerRow = (sheet, { minRow } = {}, translateObject) => {
  try {
    // Keep disclaimer from influencing auto-fit widths: call after autoFitColumns.
    // For image tabs, allow the caller to push it below the image(s).
    // `translateObject` is the i18n callback used across the app; here we rely on
    // the translation key `Disclaimer_text` to populate the disclaimer message.
    const minRowIndex = Number.parseInt(minRow, 10);
    if (Number.isFinite(minRowIndex) && minRowIndex > 0) {
      // We add an empty row + the disclaimer row (2 rows total).
      // Ensure the disclaimer ends up at or below minRow.
      ensureMinRowCount(sheet, Math.max(1, minRowIndex - 2));
    }
    sheet.addRow([]);
    sheet.addRow([]);
    sheet.addRow([]);
    const row = sheet.addRow([translateObject('Disclaimer_text')]);

    // Merge across 10 columns (A:J)
    const rowNumber = row.number;
    const endCol = 10;
    // ensure column exists
    sheet.getColumn(endCol);
    try {
      sheet.mergeCells(rowNumber, 1, rowNumber, endCol);
    } catch { /* noop */ }

    // Wrap long text and keep it readable.
    const cell = row.getCell(1);
    cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: false };
    cell.font = { ...(cell.font || {}), italic: true, size: 10 };
  } catch { /* noop */ }
};

// Convert cell value to string for width calculation
const cellToString = (val) => {
  if (val == null) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  if (val instanceof Date) return val.toLocaleString();
  if (Array.isArray(val)) return val.join(', ');
  if (typeof val === 'object') {
    return val.richText?.map(rt => rt.text || '').join('')
      || val.text
      || val.hyperlink
      || (val.result != null ? String(val.result) : '')
      || (val.formula ? String(val.formula) : '');
  }
  return String(val);
};

// Auto-fit column widths based on cell contents (approximate character width)
const autoFitColumns = (sheet, { min = 10, max = 60 } = {}) => {
  try {
    const { columnCount = 0, rowCount = 0 } = sheet;
    if (!columnCount || !rowCount) return;

    for (let c = 1; c <= columnCount; c += 1) {
      let maxLen = 0;
      for (let r = 1; r <= rowCount; r += 1) {
        const text = cellToString(sheet.getRow(r).getCell(c).value);
        const lineMax = text.split(/\r?\n/).reduce((m, line) => Math.max(m, line.length), 0);
        if (lineMax > maxLen) maxLen = lineMax;
      }
      sheet.getColumn(c).width = Math.min(Math.max(maxLen + 2, min), max);
    }
  } catch { /* noop */ }
};

// Sanitize and dedupe sheet names (Excel: max 31 chars, allow only alphanumerics, space, -, _, ( ))
const makeValidSheetName = (name, usedSet) => {
  // replace any char outside the allowed set
  const INVALID = /[^A-Za-z0-9 \-_()]/g;
  const ensureLen = (n) => (n && n.length > 31 ? n.slice(0, 31) : n || 'Sheet');
  const base = ensureLen(String(name).replace(INVALID, '-').trim());
  let final = base;
  let i = 2;
  while (usedSet.has(final)) {
    const suffix = ` (${i})`;
    final = ensureLen(base.slice(0, 31 - suffix.length) + suffix);
    i += 1;
  }
  usedSet.add(final);
  return final;
};

/**
 * Main Excel generator (NO loops, NO generators)
 *
 * @param {Array<{label: string, data: any, headerRowCount?: number|number[]}>} googleSheetsData
 *   List of tabs to generate. Each item becomes one worksheet.
 * @param {Record<string, any>} diagramMap
 *   Map of diagram label -> image input. Inputs can be canvas, data URL, SVG string,
 *   remote URL, or objects like { src } / { svg, width, height }.
 * @param {string} text
 *   Base name for the exported file (the function appends `.xlsx`).
 * @param {(key: string) => string} translateObject
 *   Translation function used to resolve disclaimer text.
 */
export const generateExcelWithImages = (
  googleSheetsData,
  diagramMap,
  text,
  translateObject
) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Winflows';
  workbook.created = new Date();
  const imagePromises = [];
  const usedSheetNames = new Set();
  googleSheetsData.forEach(item => {
    if (!item[item.label]) return;

    const rawLabel = item.label || 'Sheet';
    const sheetName = makeValidSheetName(rawLabel, usedSheetNames);
    const sheet = workbook.addWorksheet(sheetName);

    // Diagram sheets
    if (rawLabel.includes('Diagram')) {
      const titleRow = sheet.addRow([rawLabel]);
      try {
        // Merge 5 columns (A:E) for the label row
        sheet.mergeCells('A1', 'L1');
        titleRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'left', wrapText: false };
      } catch { /* noop */ }
      applyBlueHeaderStyle(titleRow);
      sheet.addRow([]);

      let maxImageBottomRow = 0;

      // Handle EDI Schematic Diagram - find all EDI diagrams in diagramMap
      if (rawLabel === 'EDI Schematic Diagram') {
        const ediDiagrams = normalizeDiagramInputs(diagramMap, (key) => key.includes('EDI Schematic'));

        if (ediDiagrams.length > 0) {
          // Pre-calculate row positions for each diagram.
          // ExcelJS positions images using row/column anchors; we reserve a fixed
          // number of rows per image to avoid overlap between multiple diagrams.
          const IMAGE_HEIGHT_ROWS = 25; // rows each image takes
          const SPACING_ROWS = 3; // rows between diagrams (label + empty + gap)
          ediDiagrams.forEach(({ label, imageInput }, index) => {
            // Calculate starting row for this diagram
            const baseRow = 3 + (index * (IMAGE_HEIGHT_ROWS + SPACING_ROWS));
            // Add label for each EDI diagram
            const labelRow = sheet.getRow(baseRow);
            labelRow.getCell(1).value = label;
            try {
              // Merge 5 columns (A:E) for the label row
              sheet.mergeCells(baseRow, 1, baseRow, 5);
              labelRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'left', wrapText: false };
            } catch { /* noop */ }
            labelRow.font = { bold: true, size: 11 };
            labelRow.commit();

            if (imageInput) {
              // Position image 2 rows after label
              const imageRow = baseRow + 2;
              imagePromises.push(insertImage(workbook, sheet, imageInput, imageRow));

              const dims = getImageDims(imageInput);
              const span = imageHeightToRowSpan(dims.height);
              maxImageBottomRow = Math.max(maxImageBottomRow, imageRow + span + 2);
            }
          });
        }

        appendDisclaimerRow(sheet, { minRow: maxImageBottomRow || undefined }, translateObject);
        return;
      }

      // Handle DSSRO Schematic Diagram - find all DSSRO diagrams
      if (rawLabel === 'DSSRO Schematic Diagram') {
        const dssroDiagrams = normalizeDiagramInputs(diagramMap, (key) => key.includes('DSSRO') || key.includes('CLRO'));

        if (dssroDiagrams.length > 0) {
          const IMAGE_HEIGHT_ROWS = 25;
          // Extra spacing to account for heading + description rows and a blank
          // spacer row before the image.
          const SPACING_ROWS = 6;
          dssroDiagrams.forEach(({ imageInput }, index) => {
            const baseRow = 3 + (index * (IMAGE_HEIGHT_ROWS + SPACING_ROWS));
            const labelRow = sheet.getRow(baseRow);
            // Row 1 (Heading)
            labelRow.getCell(1).value = imageInput?.text || '';;
            try {
              sheet.mergeCells(baseRow, 1, baseRow, 5); // A:E
              labelRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'left', wrapText: false };
            } catch { /* noop */ }
            labelRow.font = { bold: true, size: 11 };
            labelRow.commit();

            // Row 2 (Text)
            const textRow1 = sheet.getRow(baseRow + 1);
            textRow1.getCell(1).value = imageInput?.text1 || '';
            try {
              sheet.mergeCells(baseRow + 1, 1, baseRow + 1, 5);
              textRow1.getCell(1).alignment = { vertical: 'middle', horizontal: 'left', wrapText: false };
            } catch { /* noop */ }
            textRow1.font = { size: 10 };
            textRow1.commit();

            if (imageInput) {
              // Place image below the 3 text rows (leave 1 empty row)
              const imageRow = baseRow + 4;
              imagePromises.push(insertImage(workbook, sheet, imageInput, imageRow));

              const dims = getImageDims(imageInput);
              const span = imageHeightToRowSpan(dims.height);
              maxImageBottomRow = Math.max(maxImageBottomRow, imageRow + span + 2);
            }
          });
        }

        appendDisclaimerRow(sheet, { minRow: maxImageBottomRow || undefined }, translateObject);
        return;
      }

      // Default diagram handling (Flowsheet Diagram, etc.)
      const imageInput = diagramMap[rawLabel];
      if (imageInput) {
        imagePromises.push(insertImage(workbook, sheet, imageInput));

        const dims = getImageDims(imageInput);
        const span = imageHeightToRowSpan(dims.height);
        // Default image insertion uses startRow=2
        maxImageBottomRow = Math.max(maxImageBottomRow, 2 + span + 2);
      }

      appendDisclaimerRow(sheet, { minRow: maxImageBottomRow || undefined }, translateObject);
      return;
    }
    // Normal data sheets - support multiple header rows via item.headerRowCount
    const headerRowCount = normalizeHeaderRowCount(item.headerRowCount ?? 1);
    writeDataToSheet(sheet, item.data, headerRowCount);

    // Convert numeric strings to numbers and format all numeric cells with 2 decimals.
    applyTwoDecimalNumberFormat(sheet);

    // Merge vertically adjacent duplicates in first column (skip header rows)
    // When headerRowCount is an array (multiple tables), we merge from the first table's data start.
    const firstHeaderRowCount = Array.isArray(headerRowCount) ? (headerRowCount[0] ?? 1) : headerRowCount;
    mergeVerticalDuplicates(sheet, 1, firstHeaderRowCount + 1);
    if(rawLabel === 'Detailed RO/NF Element-Level Data'){
      sheet.mergeCells('A1:A3');
      sheet.mergeCells('B1:B3');
    }
    if(rawLabel === 'EDI Unit System-Level Summary'){
      sheet.mergeCells('A29:C29');
      sheet.getCell('A29').alignment = { vertical: 'middle', horizontal: 'center', wrapText: false };
      sheet.mergeCells('A33:C33');
      sheet.getCell('A33').alignment = { vertical: 'middle', horizontal: 'center', wrapText: false };
      sheet.mergeCells('A38:C38');
      sheet.getCell('A38').alignment = { vertical: 'middle', horizontal: 'center', wrapText: false };
    }
    // Merge duplicate header labels horizontally (only blue header cells).
    mergeHorizontalDuplicatesInHeaders(sheet);
    // Auto-fit column widths to content
    autoFitColumns(sheet);

    // Add disclaimer at the bottom of each tab
    appendDisclaimerRow(sheet, {}, translateObject);
  });

  return Promise.all(imagePromises)
    .then(() => workbook.xlsx.writeBuffer())
    .then(buffer =>
      saveAs(
        new Blob([buffer], {
          type:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        `${text}.xlsx`
      )
    );
};
